/**
 * Client-side mirror of the server pricing logic, used to render an
 * instant price estimate before the canonical /calculate-price edge
 * function returns. The server response always wins; this is for UX.
 *
 * Rules (mirrored from the server):
 *   - Base nightly rate applies for parties of 3+ guests.
 *   - Couples discount: 2 guests pay (base - couplesDiscountPct%) per night.
 *   - Taper: 4+ guests get an additional per-person reduction of
 *     `perPersonReductionPct` for each guest above 4, capped so the
 *     rate cannot drop below 60% of base.
 *   - Single occupancy charges the couples-discounted rate (treated
 *     as a party of 2 for pricing fairness).
 */

import { formatPence as fmt } from './utils/formatting.js';

const MIN_RATE_FLOOR_PCT = 60;

/**
 * Compute the per-night rate in pence for a given party size.
 *
 * @param totalGuests       Adults + children (1 minimum).
 * @param baseRatePence     Property base rate per night in pence.
 * @param couplesDiscountPct Percentage discount for 1-2 guests (e.g. 10 = 10%).
 * @param perPersonReductionPct Percentage reduction per guest above 4.
 */
export function nightlyRatePence(
  totalGuests: number,
  baseRatePence: number,
  couplesDiscountPct: number,
  perPersonReductionPct: number,
): number {
  if (!Number.isFinite(totalGuests) || totalGuests < 1) {
    throw new Error(`nightlyRatePence: invalid totalGuests=${totalGuests}`);
  }
  if (!Number.isFinite(baseRatePence) || baseRatePence <= 0) {
    throw new Error(`nightlyRatePence: invalid baseRatePence=${baseRatePence}`);
  }

  let pct = 100;

  if (totalGuests <= 2) {
    pct = 100 - clampPct(couplesDiscountPct);
  } else if (totalGuests === 3) {
    pct = 100;
  } else {
    // 4+ guests: per-person taper
    const extra = totalGuests - 4;
    pct = 100 - clampPct(perPersonReductionPct) * (extra + 1);
  }

  if (pct < MIN_RATE_FLOOR_PCT) pct = MIN_RATE_FLOOR_PCT;
  if (pct > 100) pct = 100;

  return Math.round((baseRatePence * pct) / 100);
}

function clampPct(p: number): number {
  if (!Number.isFinite(p)) return 0;
  if (p < 0) return 0;
  if (p > 100) return 100;
  return p;
}

/** Compute a discount label given guest count. */
export function discountLabel(totalGuests: number): 'couples' | 'taper' | 'none' {
  if (totalGuests <= 2) return 'couples';
  if (totalGuests >= 4) return 'taper';
  return 'none';
}

/**
 * Re-export the canonical formatter so callers that import from
 * pricing.ts have a one-stop module — matches the spec signature.
 */
export function formatPence(pence: number): string {
  return fmt(pence);
}

/**
 * Compute an estimated total breakdown locally for instant UI feedback.
 * Server response is authoritative.
 */
export function estimateBreakdown(args: {
  numNights: number;
  totalGuests: number;
  baseRatePence: number;
  couplesDiscountPct: number;
  perPersonReductionPct: number;
  depositPct: number; // e.g. 30 = 30%
}): {
  nightlyRatePence: number;
  totalPricePence: number;
  depositPence: number;
  balancePence: number;
} {
  const nightly = nightlyRatePence(
    args.totalGuests,
    args.baseRatePence,
    args.couplesDiscountPct,
    args.perPersonReductionPct,
  );
  const total = nightly * Math.max(0, args.numNights);
  const deposit = Math.round((total * clampPct(args.depositPct)) / 100);
  return {
    nightlyRatePence: nightly,
    totalPricePence: total,
    depositPence: deposit,
    balancePence: total - deposit,
  };
}
